import { PAYROLL_CONFIG } from './payroll.config';

export type PeriodMap = Record<string | number, number>;

export interface PayCalculation {
  periods: PeriodMap;
  gross: number;
  taxRate: number;
  taxWithheld: number;
  net: number;
  periodSum: number;
}

/**
 * Round money half-up to 2 decimal places.
 * Avoids IEEE float drift for currency display/storage.
 */
export function roundMoney(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const sign = value < 0 ? -1 : 1;
  const abs = Math.abs(value);
  return (sign * Math.round((abs + Number.EPSILON) * 100)) / 100;
}

/**
 * Normalize period values: blank/undefined → 0. Keeps periods 1..maxPeriods
 * and preserves any custom keys (e.g. '3A', '3B').
 * Automatically clamps percentage scores to 100 max ($100 cap per period).
 */
export function normalizePeriods(
  periods: Partial<Record<number | string, number | null | undefined>>,
  maxPeriods: number = PAYROLL_CONFIG.maxPeriods
): PeriodMap {
  const result: PeriodMap = {};

  for (let i = 1; i <= maxPeriods; i += 1) {
    const raw = periods[i] ?? periods[String(i)];
    const n = typeof raw === 'number' ? raw : Number(raw);
    result[i] = Number.isFinite(n) ? roundMoney(Math.min(100, Math.max(0, n))) : 0;
  }

  // Also preserve and clamp custom keys like '3A', '3B'
  Object.keys(periods || {}).forEach((key) => {
    const isStandardIndex = /^\d+$/.test(key) && Number(key) >= 1 && Number(key) <= maxPeriods;
    if (!isStandardIndex) {
      const raw = periods[key];
      const n = typeof raw === 'number' ? raw : Number(raw);
      if (Number.isFinite(n)) {
        result[key] = roundMoney(Math.min(100, Math.max(0, n)));
      }
    }
  });

  return result;
}

/**
 * Pay formula (matches Google Sheets):
 *   gross = sum(period_1 … period_8)  // each % point = $1 by default
 *   tax   = round(gross * tax_rate, 2)
 *   net   = round(gross - tax, 2)
 */
export function calculatePay(
  periodsInput: Partial<Record<number | string, number | null | undefined>>,
  taxRate: number = PAYROLL_CONFIG.taxRate,
  dollarsPerPercentagePoint: number = PAYROLL_CONFIG.dollarsPerPercentagePoint
): PayCalculation {
  const periods = normalizePeriods(periodsInput);
  const periodSum = Object.values(periods).reduce((sum, v) => sum + v, 0);
  const gross = roundMoney(periodSum * dollarsPerPercentagePoint);
  const rate = Number.isFinite(taxRate) ? taxRate : PAYROLL_CONFIG.taxRate;
  const taxWithheld = roundMoney(gross * rate);
  const net = roundMoney(gross - taxWithheld);

  return {
    periods,
    gross,
    taxRate: rate,
    taxWithheld,
    net,
    periodSum,
  };
}

export function buildPayPeriodKey(start: string | Date, end: string | Date): string {
  const toKey = (d: string | Date) => {
    if (typeof d === 'string') {
      // Already ISO date (YYYY-MM-DD)
      if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
      const parsed = new Date(d);
      return parsed.toISOString().slice(0, 10);
    }
    return d.toISOString().slice(0, 10);
  };
  return `${toKey(start)}_${toKey(end)}`;
}

export function formatMoney(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
}
